import type { IEventProperties, IEventPropertiesWithPageChapter } from './models/eventProperties';
import type { RouteLocationNormalized } from 'vue-router';
import type { ERouteName } from '~/app/router';
import type { TEventName } from './models/eventName';

import { routeNameToTrackerPages } from './models/pages';
import { pageToTrackerChapters } from './models/chapters';

import { useUserStore, useCityStore } from '~/features/store';
import { getInstallId } from '#shared/helpers/getInstallId';
import { useMedia } from '@mm-frontend/mm-web-ui-kit';
import { useAuthSDK } from '~/features/auth-sdk';
import { CONFIG } from './config';
import { isDevBuildMode } from '#shared/helpers/buildMode';

export class ClickStreamTracker {
  private static instance: ClickStreamTracker | null = null;

  private pendingEvents: Array<{
    eventName: TEventName;
    payload: IEventPropertiesWithPageChapter;
  }> = [];

  private get SDK() {
    return (globalThis as any).ClickStreamSDK_WEB;
  }

  public static getInstance(): ClickStreamTracker {
    if (!ClickStreamTracker.instance) {
      ClickStreamTracker.instance = new ClickStreamTracker();
    }
    return ClickStreamTracker.instance;
  }

  private constructor() {}

  public track(payload: IEventProperties, route: RouteLocationNormalized) {
    if (isDevBuildMode() && !route.name) {
      throw new Error("Route is undefined in track - ensure it's passed from component");
    }
    payload.page = payload.page || routeNameToTrackerPages(route.name as ERouteName);
    payload.chapter = payload.chapter || pageToTrackerChapters(payload.page);

    const typedPayload = payload as IEventPropertiesWithPageChapter;
    this.trackWithEventName(this.buildEventName(typedPayload), typedPayload);
  }

  /*
  это публичный метод для исключений, в которых ивент формируется не из payload и имеет особенные page/chapter
  */
  public trackWithEventName(eventName: TEventName, payload: IEventPropertiesWithPageChapter) {
    if (!this.isSdkReady()) {
      // Буферизуем событие до готовности SDK
      this.pendingEvents.push({ eventName, payload });
      return;
    }

    try {
      const eventProperties = this.getEventProperties(payload);
      const appProperties = this.getAppProperties();
      const deviceProperties = this.getDeviceProperties();
      const abProperties = this.getAbProperties();
      const userProperties = this.getUserProperties();
      if (isDevBuildMode()) {
        console.log(eventName, payload);
      }
      this.SDK.ClickStreamSDK.trackEvent(
        eventName,
        eventProperties,
        appProperties,
        deviceProperties,
        userProperties,
        abProperties
      );
    } catch (e) {
      console.error('Clickstream: Ошибка при отправке события:', e);
    }
  }

  public notifyReady() {
    if (!this.isSdkReady() || this.pendingEvents.length === 0) return;
    const queue = [...this.pendingEvents];
    this.pendingEvents = [];
    for (const { eventName, payload } of queue) {
      this.trackWithEventName(eventName, payload);
    }
  }

  private isSdkReady() {
    try {
      return !!(
        this.SDK &&
        this.SDK.ClickStreamSDK &&
        typeof this.SDK.ClickStreamSDK.trackEvent === 'function'
      );
    } catch {
      return false;
    }
  }

  private filterUndefined(map: Map<string, any>): Map<string, any> {
    const filtered = new Map<string, any>();
    for (const [key, value] of map.entries()) {
      if (value !== undefined) {
        filtered.set(key, value);
      } else {
        console.error(`Undefined value for key "${key}" in trackEvent payload`);
      }
    }
    return filtered;
  }

  private getEventProperties(payload: IEventProperties) {
    const { currentCity } = useCityStore();

    const defaultPayload = {
      city: currentCity.value.name,
      service_name: CONFIG.SERVICE_NAME,
    };

    return this.SDK.kotlin.collections.KtMap.fromJsMap(
      this.filterUndefined(
        new Map(Object.entries({ ...defaultPayload, ...this.stringifyPayload(payload) }))
      )
    );
  }

  private getAppProperties() {
    return new this.SDK.ru.tander.clickstream.core.properties.AppProperties(
      CONFIG.APP_TYPE,
      this.SDK.kotlin.collections.KtMap.fromJsMap(this.filterUndefined(new Map([])))
    );
  }

  private getDeviceProperties() {
    const { isMobileOrTablet } = useMedia();
    return new this.SDK.ru.tander.clickstream.core.properties.DeviceProperties(
      this.SDK.kotlin.collections.KtMap.fromJsMap(
        this.filterUndefined(
          new Map([
            ['device_id', getInstallId()],
            ['platform', isMobileOrTablet.value ? 'WEB_MOBILE' : 'WEB_DESKTOP'],
          ])
        )
      )
    );
  }

  private getAbProperties() {
    return new this.SDK.ru.tander.clickstream.core.properties.AbProperties('market');
  }

  private getUserProperties() {
    const { isAuthorized } = useAuthSDK();
    const { marketCustomer, profile } = useUserStore();

    const mindboxDeviceUUID = useCookie('mindboxDeviceUUID');

    // @todo подключить пиксели (appflyer, yandex, mindbox)
    return new this.SDK.ru.tander.clickstream.core.properties.UserProperties(
      this.SDK.kotlin.collections.KtMap.fromJsMap(
        this.filterUndefined(
          new Map([
            ['magnit_id', this.getStringOrUnknown(profile.value.id)],
            ['market_customer_id', this.getStringOrUnknown(marketCustomer.value.customerId)],
            ['market_account_id', this.getStringOrUnknown(marketCustomer.value.accountId)],
            ['install_id', this.getStringOrUnknown(getInstallId())],
            ['appsflyer_id', this.getStringOrUnknown()],
            ['mindbox_id', mindboxDeviceUUID.value ?? ''],
            ['user_auth', isAuthorized.value ? 'true' : 'false'],
          ])
        )
      )
    );
  }

  private stringifyPayload(payload: IEventProperties) {
    const stringifiedPayload = {} as Record<string, string>;

    Object.entries(payload).forEach(([key, value]) => {
      stringifiedPayload[key] =
        typeof value === 'object' ? JSON.stringify(value) : this.getStringOrUnknown(value);
    });

    return stringifiedPayload;
  }

  private getStringOrUnknown(value?: any): string {
    if (value === undefined || value === null) {
      return 'unknown';
    }

    const str = String(value).trim();
    return str || 'unknown';
  }

  public buildEventName(payload: IEventProperties): TEventName {
    const page = payload.page;
    const chapter = payload.chapter;
    const block = !payload.block || payload.block === 'unknown' ? null : payload.block;
    const element = !payload.element || payload.element === 'unknown' ? null : payload.element;
    const action = payload.action;

    const eventNameBuilder = [chapter, page, block, element, action].filter((x) => x);

    return eventNameBuilder.join('_') as TEventName;
  }
}

export const tracker = ClickStreamTracker.getInstance();

export type { TEventName } from './models/eventName';
export { type TTrackerPages, ETrackerPages } from './models/pages';
export { type TTrackerChapters, ETrackerChapters } from './models/chapters';
export { type TTrackerActions, ETrackerActions } from './models/actions';
